import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography,
} from '@mui/material';
import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import { CashFlowGroup, CashFlowStatement as CashFlowStatementData, Segment } from '../../interfaces/accounting.interface';
import { CashFlowService } from '../../services/cashFlow.service';
import { FinancialStatementService } from '../../services/financialStatement.service';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';
import { downloadCsv } from '../../utils/csv';
import { formatMoney } from '../../utils/format';
import { firstOfYearLocalIso, formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const amountCell = { textAlign: 'right' as const, whiteSpace: 'nowrap' as const };

export const cashFlowCsv = (r: CashFlowStatementData): { headers: string[]; rows: (string | number)[][] } => {
    const rows: (string | number)[][] = [];
    const section = (title: string, groups: CashFlowGroup[], total?: [string, number]) => {
        rows.push([title, '', '']);
        groups.forEach(g => {
            rows.push([g.groupCode, g.groupName, g.amount]);
            if (g.lines.length > 1 || g.groupAccountId) {
                g.lines.forEach(l => rows.push([l.accountCode, `  ${l.accountName}`, l.amount]));
            }
        });
        if (total) rows.push(['', total[0], total[1]]);
    };
    rows.push(['', 'Net profit for the period', r.netProfit]);
    section('Adjustments for non-cash items', r.nonCashAdjustments, ['Total non-cash adjustments', r.totalNonCashAdjustments]);
    section('Changes in working capital', r.workingCapital, ['Total working capital changes', r.totalWorkingCapital]);
    rows.push(['', 'Net cash from operating activities', r.netCashFromOperating]);
    section('Investing activities', r.investing, ['Net cash from investing activities', r.netCashFromInvesting]);
    section('Financing activities', r.financing, ['Net cash from financing activities', r.netCashFromFinancing]);
    rows.push(['', 'Net change in cash', r.netChangeInCash]);
    rows.push(['', 'Opening cash', r.openingCash]);
    rows.push(['', 'Closing cash (statement)', r.closingCash]);
    rows.push(['', 'Actual cash and bank balance', r.actualClosingCash]);
    rows.push(['', 'Difference', r.reconciliationDifference]);
    return { headers: ['Code', 'Description', 'Amount'], rows };
};

const GroupRows: React.FC<{ groups: CashFlowGroup[] }> = ({ groups }) => {
    const [open, setOpen] = useState<Record<string, boolean>>({});
    return (
        <>
            {groups.map(g => {
                const key = g.groupAccountId ?? g.groupCode;
                const expandable = !!g.groupAccountId;
                return (
                    <React.Fragment key={key}>
                        <TableRow hover>
                            <TableCell sx={{ pl: 4, width: 48 }}>
                                {expandable && (
                                    <IconButton data-help="accounting.cashFlow.action.toggle"
                                        size="small"
                                        aria-label={`Toggle ${g.groupName}`}
                                        onClick={() => setOpen(o => ({ ...o, [key]: !o[key] }))}
                                    >
                                        {open[key] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                )}
                            </TableCell>
                            <TableCell>{g.groupCode} - {g.groupName}</TableCell>
                            <TableCell sx={amountCell}>{formatMoney(g.amount)}</TableCell>
                        </TableRow>
                        {expandable && open[key] && g.lines.map(l => (
                            <TableRow key={l.accountId}>
                                <TableCell />
                                <TableCell sx={{ pl: 6, color: 'text.secondary' }}>
                                    <RouterLink to={`/accounting/accounts/${l.accountId}/ledger`}>{l.accountCode}</RouterLink>
                                    {' '}{l.accountName}
                                </TableCell>
                                <TableCell sx={{ ...amountCell, color: 'text.secondary' }}>{formatMoney(l.amount)}</TableCell>
                            </TableRow>
                        ))}
                    </React.Fragment>
                );
            })}
        </>
    );
};

const Heading: React.FC<{ text: string }> = ({ text }) => (
    <TableRow sx={{ bgcolor: 'action.hover' }}>
        <TableCell colSpan={3}><strong>{text}</strong></TableCell>
    </TableRow>
);

const Total: React.FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
    <TableRow sx={{ borderTop: strong ? '2px solid' : undefined }}>
        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>{label}</TableCell>
        <TableCell sx={{ ...amountCell, fontWeight: 700 }}>{formatMoney(value)}</TableCell>
    </TableRow>
);

const CashFlowStatement: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<CashFlowStatementData | null>(null);
    const [loading, setLoading] = useState(false);
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [segments, setSegments] = useState<Segment[]>([]);
    const [segmentId, setSegmentId] = useState('');

    useEffect(() => {
        SegmentService.getByInstitution()
            .then(page => setSegments(page.content ?? []))
            .catch(err => toast.showApiError(err, 'Failed to load segments'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setReport(await CashFlowService.getStatement(from, to, segmentId || undefined));
        } catch (err) {
            toast.showApiError(err, 'Failed to load cash flow statement');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, segmentId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Exports the report on screen (its own dates), never the unapplied inputs.
    const handleExport = () => {
        if (!report) return;
        const { headers, rows } = cashFlowCsv(report);
        downloadCsv(`cash-flow-${report.fromDate}-to-${report.toDate}.csv`, headers, rows);
    };

    // W3-K: server-rendered PDF/XLSX of the report on screen (its own dates and segment).
    const handleServerExport = async (format: 'pdf' | 'xlsx') => {
        if (!report) return;
        try {
            await FinancialStatementService.downloadCashFlow(report.fromDate, report.toDate, format, report.segmentFilter ?? undefined);
        } catch (err) {
            toast.showApiError(err, 'Download failed');
        }
    };

    return (
        <Box data-help-page="accounting.cashFlow.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Cash Flow Statement</Typography>
            <Box data-help-section="accounting.cashFlow.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="accounting.cashFlow.from"
                    label="From" type="date" size="small" value={from}
                    onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }}
                />
                <TextField data-help="accounting.cashFlow.to"
                    label="To" type="date" size="small" value={to}
                    onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }}
                />
                <SearchableSelect data-help="accounting.cashFlow.segment"
                    options={segments}
                    value={segmentId}
                    onChange={id => setSegmentId(id)}
                    getOptionLabel={seg => `${seg.code} — ${seg.name}`}
                    label="Segment"
                    placeholder="All Segments"
                    sx={{ flex: '1 1 200px', maxWidth: 300 }}
                />
                <Button data-help="accounting.cashFlow.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="accounting.cashFlow.exportCsv" variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={loading || !report}>
                    Export CSV
                </Button>
                <Button data-help="accounting.cashFlow.exportPdf" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => handleServerExport('pdf')} disabled={loading || !report}>PDF</Button>
                <Button data-help="accounting.cashFlow.exportXlsx" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => handleServerExport('xlsx')} disabled={loading || !report}>Excel</Button>
            </Box>

            {report && !report.reconciled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The statement does not tie to the actual cash and bank balances (difference {formatMoney(report.reconciliationDifference)}).
                    {report.unclassifiedAccounts.length > 0 && (
                        <>
                            {' '}Unclassified accounts: {report.unclassifiedAccounts.map(u => `${u.accountCode} ${u.accountName}`).join(', ')}.
                            {' '}<RouterLink to="/accounting/cash-flow-classification">Classify them</RouterLink>.
                        </>
                    )}
                </Alert>
            )}
            {report && report.notes.filter(n => !n.includes('no cash flow category')).map(n => (
                <Alert key={n} severity="info" sx={{ mb: 2 }}>{n}</Alert>
            ))}

            {report && (
                <TableContainer data-help-section="accounting.cashFlow.table" component={Paper} variant="outlined">
                    <Table size="small" sx={{ minWidth: 560 }}>
                        <TableBody>
                            <Heading text={`Operating activities (${formatDate(report.fromDate)} to ${formatDate(report.toDate)})`} />
                            <TableRow>
                                <TableCell colSpan={2}>Net profit for the period</TableCell>
                                <TableCell sx={amountCell}>{formatMoney(report.netProfit)}</TableCell>
                            </TableRow>
                            {report.nonCashAdjustments.length > 0 && <Heading text="Adjustments for non-cash items" />}
                            <GroupRows groups={report.nonCashAdjustments} />
                            {report.workingCapital.length > 0 && <Heading text="Changes in working capital" />}
                            <GroupRows groups={report.workingCapital} />
                            <Total label="Net cash from operating activities" value={report.netCashFromOperating} />
                            <Heading text="Investing activities" />
                            <GroupRows groups={report.investing} />
                            <Total label="Net cash from investing activities" value={report.netCashFromInvesting} />
                            <Heading text="Financing activities" />
                            <GroupRows groups={report.financing} />
                            <Total label="Net cash from financing activities" value={report.netCashFromFinancing} />
                            <Total label="Net change in cash" value={report.netChangeInCash} strong />
                            <TableRow>
                                <TableCell colSpan={2}>Opening cash and bank</TableCell>
                                <TableCell sx={amountCell}>{formatMoney(report.openingCash)}</TableCell>
                            </TableRow>
                            <Total label="Closing cash and bank (statement)" value={report.closingCash} />
                            <TableRow>
                                <TableCell colSpan={2}>Actual cash and bank balance (ledger)</TableCell>
                                <TableCell sx={amountCell}>{formatMoney(report.actualClosingCash)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2} sx={{ color: report.reconciled ? 'success.main' : 'error.main' }}>
                                    Reconciliation difference
                                </TableCell>
                                <TableCell sx={{ ...amountCell, color: report.reconciled ? 'success.main' : 'error.main' }}>
                                    {formatMoney(report.reconciliationDifference)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default CashFlowStatement;
